import { useId, useState, type ComponentProps } from 'react';
import { IconChevronDown, IconCornerDownRight } from '@tabler/icons-react';
import type { FormField } from '../../types';
import type { ExploreExchange } from '../../lib/explore-response-exchanges';
import type { StructuredResponseAnswer } from '../../lib/response-completion';
import { AnimatedPresence } from '../ui/AnimatedPresence';
import { StructuredAnswerVisual } from './StructuredAnswerVisual';
import { InsightText } from './report/InsightText';

/** Display the original Explore exchanges while keeping its normalized value accessible. */
export function StoredResponseAnswer({ answer, field, exchanges = [], insights }: {
  answer: StructuredResponseAnswer;
  field?: FormField;
  exchanges?: ExploreExchange[];
  insights?: ComponentProps<typeof InsightText>['insights'];
}) {
  const [savedOpen, setSavedOpen] = useState(false);
  const savedId = useId();
  const replies = exchanges.flatMap(exchange => exchange.replies);
  const hasDialogue = replies.length > 0 && (replies.length > 1 || exchanges.some(exchange => exchange.prompt));
  if (!hasDialogue) return <div>
    <dt className="text-sm text-muted-foreground">{answer.label}</dt>
    <dd className="mt-1 text-foreground whitespace-pre-wrap break-words"><InsightText text={answer.displayValue} insights={insights} />
      <StructuredAnswerVisual field={field} value={answer.value} />
    </dd>
  </div>;

  return <div className="min-w-0">
    <dt className="text-sm font-medium text-foreground">{answer.label}</dt>
    <dd className="mt-1.5">
      <ol aria-label={answer.label} className="space-y-3 break-words">
        {exchanges.filter(exchange => exchange.prompt || exchange.replies.length).map(exchange => <li key={exchange.key} className={exchange.prompt ? 'border-s-2 border-border ps-3' : undefined}>
          {exchange.prompt && <p className="flex items-baseline gap-2 text-sm font-medium text-muted-foreground">
            <IconCornerDownRight size={14} aria-hidden="true" className="shrink-0 self-start mt-0.5 text-accent-soft" />
            <span className="min-w-0 whitespace-pre-wrap">{exchange.prompt}</span>
          </p>}
          {exchange.replies.map((reply, index) => <p key={index} className="mt-1 whitespace-pre-wrap leading-relaxed text-foreground">
            <InsightText text={reply} insights={insights} />
          </p>)}
        </li>)}
      </ol>
      {answer.displayValue !== replies.at(-1) && <div className="mt-3">
        <button type="button" aria-expanded={savedOpen} aria-controls={savedId} onClick={() => setSavedOpen(open => !open)}
          className="inline-flex items-center gap-1.5 rounded text-sm text-muted-foreground hover:text-foreground focus-visible:outline-2 focus-visible:outline-ring">
          Saved answer
          <IconChevronDown size={14} aria-hidden="true" className={`transition-transform motion-reduce:transition-none ${savedOpen ? 'rotate-180' : ''}`} />
        </button>
        <div id={savedId}><AnimatedPresence show={savedOpen}>
          <p className="pt-2 whitespace-pre-wrap break-words leading-relaxed text-foreground"><InsightText text={answer.displayValue} insights={insights} /></p>
        </AnimatedPresence></div>
      </div>}
    </dd>
  </div>;
}
